import d from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Tabs, Typography } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CiSquareChevLeft, CiSquareChevRight } from 'react-icons/ci';
import api from 'src/api';
import BookingCalendar from 'src/components/booking-calendar';
import CalendarBulkPanel from 'src/components/calendar-bulk-panel';
import CalendarSyncPanel from 'src/components/calendar-sync-panel';
import Spinner from 'src/components/spinner';
import { useCalendarEntries } from 'src/hooks/calendar-entries.queries';
import useSelectedLang from 'src/hooks/use-selected-lang';
import { getMonthName, hasLocalMonths, months } from 'src/utils/calendar-info';
import {
  ALL_WEEKDAYS,
  entryKey,
  getMonthsData,
  getTargetDates,
  todayKey
} from 'src/utils/calendar-range';

const currentYear = d().year();
const years = [
  currentYear - 2,
  currentYear - 1,
  currentYear,
  currentYear + 1,
  currentYear + 2,
  currentYear + 3
];

const MONTHS_TO_SHOW = 3;

function ManageCalendar() {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('propertyId');
  const navigate = useNavigate();
  const roomTypeId = searchParams.get('roomId');
  const [year, setYear] = useState(d().year());
  const [month, setMonth] = useState(d().month());
  const [range, setRange] = useState(() => ({
    start: todayKey(),
    end: todayKey()
  }));
  const [daysOfWeek, setDaysOfWeek] = useState(ALL_WEEKDAYS);
  const [updatingCalendar, setUpdatingCalendar] = useState(false);
  const [roomId, setRoomId] = useState(roomTypeId);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth <= 1024 && window.innerWidth > 768
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { data: property } = useQuery({
    queryKey: [
      'property',
      propertyId,
      ['propertyName', 'propertyType', 'apartment']
    ],
    queryFn: async ({ queryKey }) => {
      const { data: { property = {} } = {} } = await api.get(
        `/properties/find/${propertyId}?select=${queryKey?.[2]?.join?.(
          ' '
        )}&populate=apartment.prices.currency`
      );
      return property;
    }
  });

  const isHotel = property?.propertyType === 'hotel';

  const { data: rooms = [] } = useQuery({
    queryKey: ['distinct-rooms', propertyId],
    enabled: isHotel,
    queryFn: async () => {
      const { data: { rooms = [] } = {} } = await api.get(
        `/hotel-rooms/${propertyId}/distinct?select=roomName displayName currency price&populate=currency&populateFrom=currencies`
      );
      if (!roomTypeId) {
        setRoomId(rooms[0]?.roomTypeId);
        navigate(
          `/dashboard/manage/rate-and-availability/calendar?propertyId=${propertyId}&roomId=${rooms[0]?.roomTypeId}`
        );
      }
      return rooms;
    }
  });

  useEffect(() => {
    if (property) {
      if (property.propertyType === 'hotel') {
        if (roomId) setLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [property, roomId]);

  const room = rooms.find(r => r.roomTypeId === roomId);

  // Month names for ar/de/fr/es are hand-written and rendered with
  // `notranslate` so the Google Translate widget cannot mistranslate them.
  const lang = useSelectedLang();
  const pinMonths = hasLocalMonths(lang);

  const monthsData = useMemo(
    () => getMonthsData(year, month, MONTHS_TO_SHOW, lang),
    [year, month, lang]
  );
  const visibleFrom = monthsData[0].from;
  const visibleTo = monthsData[monthsData.length - 1].to;

  const {
    calendarEntries = [],
    isFetching: isEntriesFetching,
    error: entriesError
  } = useCalendarEntries({
    from: visibleFrom,
    to: visibleTo,
    propertyId,
    roomId: isHotel ? roomId : undefined,
    enabled: !loading && (!isHotel || !!roomId)
  });

  // Latest entry per date (API returns newest first).
  const entriesByKey = useMemo(() => {
    const map = {};
    calendarEntries.forEach(entry => {
      const key = entryKey(entry);
      if (!map[key]) map[key] = entry;
    });
    return map;
  }, [calendarEntries]);

  const targetDates = useMemo(
    () => getTargetDates(range, daysOfWeek),
    [range, daysOfWeek]
  );

  const goToMonth = useCallback(date => {
    setYear(date.year());
    setMonth(date.month());
  }, []);

  // `navigate` brings the calendar to the start date when it was picked from
  // the date pickers and is outside the months currently on screen.
  const handleRangeChange = useCallback(
    (next, { navigate: shouldNavigate = false } = {}) => {
      setRange(next);
      if (shouldNavigate && next?.start) {
        const start = d(next.start);
        if (
          start.isBefore(visibleFrom, 'day') ||
          start.isAfter(visibleTo, 'day')
        ) {
          goToMonth(start);
        }
      }
    },
    [visibleFrom, visibleTo, goToMonth]
  );

  const shiftMonth = delta =>
    goToMonth(d(new Date(year, month, 1)).add(delta, 'month'));

  const containerPadding = isMobile ? '10px' : isTablet ? '15px' : '20px';
  const containerGap = isMobile ? '10px' : isTablet ? '15px' : '20px';
  const titleLevel = isMobile ? 3 : isTablet ? 3 : 2;
  const subtitleLevel = isMobile ? 4 : isTablet ? 4 : 3;
  const selectSize = isMobile ? 'middle' : 'large';
  const iconSize = isMobile ? 28 : isTablet ? 32 : 38;
  const selectWidth = isMobile ? '45%' : isTablet ? '110px' : '140px';

  if (loading) return <Spinner />;

  return (
    <div
      style={{
        padding: containerPadding,
        display: 'flex',
        flexDirection: 'column',
        gap: containerGap,
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}
    >
      <Typography.Title level={titleLevel} style={{ margin: 0 }}>
        Calendar
      </Typography.Title>
      <Typography.Title level={subtitleLevel} style={{ margin: 0 }}>
        {property?.propertyName || ''}
      </Typography.Title>

      {isHotel && roomId && (
        <Select
          size={selectSize}
          style={{ width: '100%' }}
          value={roomId}
          onChange={value => {
            setRoomId(value);
            navigate(
              `/dashboard/manage/rate-and-availability/calendar?propertyId=${propertyId}&roomId=${value}`
            );
          }}
          options={rooms.map(r => ({
            value: r.roomTypeId,
            label: r.displayName || r.roomName
          }))}
        />
      )}

      <div
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          width: '100%',
          flexWrap: isMobile || isTablet ? 'wrap' : 'nowrap'
        }}
      >
        <Select
          size={selectSize}
          style={{ width: selectWidth }}
          value={month}
          onChange={value => setMonth(value)}
          className={pinMonths ? 'notranslate' : undefined}
          popupClassName={pinMonths ? 'notranslate' : undefined}
          options={months.map((_, i) => ({
            value: i,
            label: pinMonths ? (
              <span className="notranslate">{getMonthName(i, lang)}</span>
            ) : (
              getMonthName(i, 'en')
            )
          }))}
        />
        <Select
          size={selectSize}
          style={{ width: selectWidth }}
          value={year}
          onChange={value => setYear(value)}
          options={years.map(y => ({ value: y, label: y }))}
        />
        <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
          Showing{' '}
          <span className={pinMonths ? 'notranslate' : undefined}>
            {monthsData[0].monthName} –{' '}
            {monthsData[monthsData.length - 1].monthName}
          </span>
        </Typography.Text>
        <div
          style={{
            color: '#2e2e2e',
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto'
          }}
        >
          <CiSquareChevLeft
            size={iconSize}
            style={{ cursor: 'pointer' }}
            onClick={() => shiftMonth(-1)}
          />
          <CiSquareChevRight
            size={iconSize}
            style={{ cursor: 'pointer' }}
            onClick={() => shiftMonth(1)}
          />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            isMobile || isTablet
              ? 'minmax(0, 1fr)'
              : 'minmax(0, 1fr) minmax(300px, 340px)',
          gap: containerGap,
          alignItems: 'stretch'
        }}
      >
        <div
          style={{
            position: 'relative',
            minHeight: isMobile ? undefined : '75vh',
            minWidth: 0
          }}
        >
          <BookingCalendar
            {...{
              monthsData,
              entriesByKey,
              isFetching: isEntriesFetching,
              error: entriesError,
              range,
              onRangeChange: handleRangeChange,
              daysOfWeek,
              updatingCalendar,
              room,
              property,
              isMobile
            }}
          />
        </div>
        <div style={{ order: isMobile || isTablet ? -1 : 0 }}>
          <Tabs
            size={isMobile ? 'small' : 'middle'}
            items={[
              {
                key: 'availability',
                label: 'Availability & price',
                children: (
                  <CalendarBulkPanel
                    {...{
                      propertyId,
                      property,
                      room,
                      roomId: isHotel ? roomId : undefined,
                      range,
                      onRangeChange: handleRangeChange,
                      daysOfWeek,
                      onDaysOfWeekChange: setDaysOfWeek,
                      targetDates,
                      entriesByKey,
                      isFetching: isEntriesFetching,
                      setUpdatingCalendar,
                      isMobile
                    }}
                  />
                )
              },
              {
                key: 'sync',
                label: 'Sync calendars',
                children: (
                  <CalendarSyncPanel
                    {...{ propertyId, property, rooms, isMobile }}
                  />
                )
              }
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export default ManageCalendar;
